import React from 'react';
import { Mail } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-6">
              Terms and Conditions
            </h1>
            <p className="text-lg text-gray-600">
              Centre for Sustainable AI
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12">
          <div className="max-w-4xl mx-auto">
            
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-gray-700 leading-relaxed mb-6">
                The Centre for Sustainable AI is committed to making its material widely available and encourages its use by governments, international organisations, industry, academia, civil society, and individuals in support of safe, responsible, and sustainable artificial intelligence.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                These Terms and Conditions set out your rights to use material owned by the Centre for Sustainable AI and made available on this website and any related websites that include a link to these Terms and Conditions (together, the "Centre Websites").
              </p>
              <p className="text-gray-700 leading-relaxed">
                By accessing a Centre Website and/or using Centre material, you agree to be bound by these Terms and Conditions. You also acknowledge that you have read and understood our Privacy Policy and Disclaimers, and agree to their terms. These Terms and Conditions, the Privacy Policy, and the Disclaimers may be updated from time to time. It is your responsibility to review them periodically.
              </p>
            </div>

            {/* Intellectual Property */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Intellectual Property and Ownership</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Unless otherwise stated, all material published on the Centre Websites is the intellectual property of the Centre for Sustainable AI and is protected by copyright or similar rights. Some content may be owned by third parties. You are responsible for identifying such content and obtaining any necessary permissions from the relevant rights holders before use.
              </p>
              <p className="text-gray-700 leading-relaxed font-medium">
                Nothing in these Terms shall be interpreted as granting any licence or right not expressly stated.
              </p>
            </div>

            {/* Categories of Content */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Categories of Content Covered</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                These Terms apply to the following types of content:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Written publications and website content</li>
                <li>Policy frameworks, guidance, and research outputs</li>
                <li>Data and analytical material</li>
                <li>Multimedia content (images, video, audio, podcasts)</li>
                <li>Centre for Sustainable AI name and logo</li>
                <li>Links to Centre Websites</li>
                <li>Media and press materials</li>
              </ul>
            </div>

            {/* 1. Written Content */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">1. Written Content</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Unless otherwise specified, Centre for Sustainable AI written content is made available for non-exclusive use, subject to the conditions below.
              </p>
              
              <h3 className="text-xl font-medium mb-4">Permitted Use</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                You may reproduce, distribute, and share Centre written content for informational, educational, research, policy, and non-commercial purposes, provided that:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Proper attribution is given to the Centre for Sustainable AI</li>
                <li>The content is not presented in a misleading or deceptive manner</li>
                <li>The content is not modified in a way that misrepresents its intent or meaning</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-6">
                <span className="font-medium">Suggested citation format:</span><br />
                Centre for Sustainable AI (year), Title, URL.
              </p>

              <h3 className="text-xl font-medium mb-4">Adaptations and Translations</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you adapt or translate Centre content:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>You must clearly indicate that the content has been adapted or translated</li>
                <li>You must not imply endorsement by the Centre</li>
                <li>You must not use the Centre logo or branding without permission</li>
              </ul>
            </div>

            {/* 2. Policy Frameworks */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">2. Policy Frameworks and Guidance</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Policy frameworks, guidance documents, and similar materials are provided for general informational purposes only. They do not constitute legal, regulatory, or professional advice.
              </p>
              <p className="text-gray-700 leading-relaxed">
                You may reference and apply these materials in policy development, governance, training, or advisory contexts, provided their non-binding and advisory nature is clearly acknowledged.
              </p>
            </div>

            {/* 3. Data and Analytical Material */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">3. Data and Analytical Material</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Data and analytical material published by the Centre may be used, extracted, shared, and incorporated into other works for informational, research, and policy purposes, unless otherwise stated.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                You are responsible for:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Verifying the accuracy and suitability of data for your purposes</li>
                <li>Identifying any third-party ownership or restrictions</li>
                <li>Providing appropriate attribution</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                The Centre makes no warranties regarding the completeness or accuracy of data and accepts no liability for reliance placed upon it.
              </p>
            </div>

            {/* 4. Multimedia Content */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">4. Multimedia Content</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Photographs, videos, audio recordings, and other multimedia assets may be subject to specific usage conditions. Where no additional terms are specified, multimedia content may be used for non-commercial, informational purposes with attribution.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Requests for extended use should be directed to:
              </p>
              <div className="flex items-center gap-3 p-4 bg-white rounded-lg border mt-4">
                <Mail className="w-5 h-5 text-gray-600" />
                <a href="mailto:support@aiesg.org" className="text-black hover:underline">
                  support@aiesg.org
                </a>
              </div>
            </div>

            {/* 5. Name and Logo */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">5. Centre for Sustainable AI Name and Logo</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Centre for Sustainable AI name and logo are legally protected.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                They may not be used without prior written permission, except where required for accurate attribution. Permission, where granted, does not imply endorsement, sponsorship, or partnership.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Requests must be submitted to:
              </p>
              <div className="flex items-center gap-3 p-4 bg-white rounded-lg border mt-4">
                <Mail className="w-5 h-5 text-gray-600" />
                <a href="mailto:support@aiesg.org" className="text-black hover:underline">
                  support@aiesg.org
                </a>
              </div>
            </div>

            {/* 6. Links */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">6. Links to Centre Websites</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Centre encourages linking to its websites without prior permission, provided that:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Links do not misrepresent the Centre's role or views</li>
                <li>The Centre's content is not framed or altered</li>
                <li>Links are not used to imply endorsement of products, services, or organisations</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                The Centre reserves the right to request removal of links that breach these conditions.
              </p>
            </div>

            {/* 7. Press and Media */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">7. Press and Media Materials</h2>
              <p className="text-gray-700 leading-relaxed">
                Press releases and media materials published on Centre Websites may be reproduced freely, provided that the Centre for Sustainable AI is cited as the original source and the content is not altered in a misleading manner.
              </p>
            </div>

            {/* Disclaimers */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Disclaimers</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The material on the Centre Websites is provided on an "as is" and "as available" basis.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                The Centre for Sustainable AI:
              </p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                <li>Makes reasonable efforts to ensure accuracy but does not guarantee completeness or correctness</li>
                <li>Reserves the right to modify, update, or remove content without notice</li>
                <li>Accepts no liability for loss, damage, or reliance arising from use of the website or its content</li>
              </ul>
              <p className="text-gray-700 leading-relaxed font-medium">
                Use of the Centre Websites and material is at your own risk.
              </p>
            </div>

            {/* No Endorsement */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">No Endorsement or Affiliation</h2>
              <p className="text-gray-700 leading-relaxed">
                You may not represent or imply that the Centre for Sustainable AI has endorsed, approved, or supported your use of its material, or that any affiliation exists, unless explicitly agreed in writing.
              </p>
            </div>

            {/* External Links */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">External Links and Third-Party Content</h2>
              <p className="text-gray-700 leading-relaxed">
                Links to external websites are provided for informational purposes only. The Centre is not responsible for the content, accuracy, or privacy practices of third-party websites.
              </p>
            </div>

            {/* Website Security */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Website Security and Availability</h2>
              <p className="text-gray-700 leading-relaxed">
                The Centre employs reasonable technical measures to ensure website security and availability. However, uninterrupted access is not guaranteed. The Centre accepts no responsibility for technical failures, malware, or service interruptions.
              </p>
            </div>

            {/* Social Media */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Social Media Disclaimer</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Content published on the Centre's social media channels reflects the views of individual contributors and does not necessarily represent institutional positions. Third-party content does not imply endorsement.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The Centre reserves the right to remove comments or content that is offensive, defamatory, misleading, or inappropriate.
              </p>
            </div>

            {/* Governing Law */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms and Conditions are governed by applicable laws of the jurisdiction in which the Centre for Sustainable AI operates, without prejudice to mandatory consumer protection provisions.
              </p>
            </div>

            {/* Contact */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Contact</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For all enquiries relating to these Terms and Conditions, please contact:
              </p>
              <div className="flex items-center gap-3 p-4 bg-white rounded-lg border">
                <Mail className="w-5 h-5 text-gray-600" />
                <a href="mailto:support@aiesg.org" className="text-black hover:underline">
                  support@aiesg.org
                </a>
              </div>
            </div>

            {/* Updates */}
            <div className="mb-12">
              <h2 className="text-2xl font-light mb-6">Updates</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms and Conditions may be updated from time to time. Continued use of the Centre Websites constitutes acceptance of the current version.
              </p>
            </div>

            {/* Last Updated */}
            <div className="pt-8 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Last updated: 28 January 2025
              </p>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
